import { useState, useEffect } from 'preact/hooks';
import { selectedKanbanTaskSignal, updateKanbanTaskDetails, deleteKanbanTask, isKanbanOpenSignal } from '../../state/kanban.store';
import { contactsSignal, selectContact, navigateToSourceMessage } from '../../state/contacts.store';
import { renderMarkdownToHtml } from '../../utils/markdownUtils';
import {
  X, MessageSquare, Save, Link as LinkIcon, Plus, Trash2, Edit3, Eye,
  Calendar, User, AlertCircle, CheckCircle2, Clock, Ban, Sparkles, HelpCircle
} from 'lucide-preact';
import type { ActionItem } from '../../types/ai';

export function TaskDetailModal() {
  const task = selectedKanbanTaskSignal.value;
  if (!task) return null;

  const [description, setDescription] = useState(task.description || '');
  const [detailedDescription, setDetailedDescription] = useState(task.detailed_description || '');
  const [notes, setNotes] = useState(task.notes || '');
  const [assignee, setAssignee] = useState(task.assignee || '');
  const [dueDate, setDueDate] = useState(task.due_date ? task.due_date.split('T')[0] : '');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>(task.priority || 'medium');
  const [status, setStatus] = useState<'pending' | 'in_progress' | 'completed' | 'cancelled'>(task.status || 'pending');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Multi-link state
  let initialLinks: string[] = [];
  try {
    if (typeof task.links === 'string') {
      initialLinks = JSON.parse(task.links);
    } else if (Array.isArray(task.links)) {
      initialLinks = task.links;
    }
  } catch (e) {
    if (task.links) initialLinks = [String(task.links)];
  }

  const [linksList, setLinksList] = useState<string[]>(initialLinks);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [activeNotesTab, setActiveNotesTab] = useState<'edit' | 'preview'>('edit');

  useEffect(() => {
    setDescription(task.description || '');
    setDetailedDescription(task.detailed_description || '');
    setNotes(task.notes || '');
    setAssignee(task.assignee || '');
    setDueDate(task.due_date ? task.due_date.split('T')[0] : '');
    setPriority(task.priority || 'medium');
    setStatus(task.status || 'pending');
    setShowDeleteConfirm(false);

    let parsed: string[] = [];
    try {
      if (typeof task.links === 'string') parsed = JSON.parse(task.links);
      else if (Array.isArray(task.links)) parsed = task.links;
    } catch (e) {
      if (task.links) parsed = [String(task.links)];
    }
    setLinksList(parsed);
  }, [task.id]);

  const handleClose = () => {
    selectedKanbanTaskSignal.value = null;
  };

  const handleGoToChat = () => {
    handleClose();
    isKanbanOpenSignal.value = false;
    navigateToSourceMessage(task.contact_phone, task.source_message_id);
  };

  const handleAddLink = () => {
    if (!newLinkUrl.trim()) return;
    let formatted = newLinkUrl.trim();
    if (!/^https?:\/\//i.test(formatted)) {
      formatted = `https://${formatted}`;
    }
    setLinksList([...linksList, formatted]);
    setNewLinkUrl('');
  };

  const handleRemoveLink = (index: number) => {
    setLinksList(linksList.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    await updateKanbanTaskDetails(task.id, {
      description,
      detailed_description: detailedDescription,
      notes,
      links: linksList,
      priority,
      status,
      assignee,
      due_date: dueDate
    });
    handleClose();
  };

  const handleDelete = async () => {
    await deleteKanbanTask(task.id);
    handleClose();
  };

  return (
    <div class="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 bg-slate-950/85 backdrop-blur-md animate-fade-in">
      <div class="w-full h-full sm:max-w-3xl sm:max-h-[90vh] bg-slate-900 sm:border sm:border-slate-800 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        
        {/* Header */}
        <div class="p-3.5 sm:p-5 border-b border-slate-800 flex items-center justify-between gap-3 bg-slate-950/60 shrink-0">
          <div class="flex items-center gap-2.5 min-w-0">
            <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
              {task.stakeholder_picture ? (
                <img src={task.stakeholder_picture} class="w-full h-full rounded-xl sm:rounded-2xl object-cover" />
              ) : (
                <Sparkles class="w-5 h-5 text-cyan-400" />
              )}
            </div>
            <div class="min-w-0">
              <h3 class="text-xs sm:text-sm font-extrabold text-slate-100 truncate">
                {task.stakeholder_name || task.contact_phone}
              </h3>
              <p class="text-[10px] sm:text-[11px] text-slate-400 font-mono truncate">
                {task.contact_phone}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={handleGoToChat}
              class="min-h-[44px] px-3 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/40 text-cyan-300 hover:text-cyan-200 text-xs font-bold rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Abrir histórico de conversa deste stakeholder"
            >
              <MessageSquare class="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              <span class="hidden xs:inline">Ir para a Conversa</span>
            </button>
            <button
              onClick={handleClose}
              class="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 rounded-xl transition cursor-pointer"
            >
              <X class="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div class="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
          
          {/* Main Title / Description */}
          <div>
            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
              Título / Resumo Curto da Tarefa
            </label>
            <input
              type="text"
              value={description}
              onInput={(e) => setDescription((e.target as HTMLInputElement).value)}
              class="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 font-semibold focus:outline-none focus:border-cyan-500 transition text-sm"
              placeholder="Ex: Enviar proposta comercial corrigida..."
            />
          </div>

          {/* Detailed Description */}
          <div>
            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center justify-between">
              <span>Descrição Detalhada do Contexto</span>
              <span class="text-[10px] text-cyan-400 font-normal">Contextualização enriquecida pela IA</span>
            </label>
            <textarea
              value={detailedDescription}
              onInput={(e) => setDetailedDescription((e.target as HTMLTextAreaElement).value)}
              rows={3}
              class="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500 transition leading-relaxed text-xs"
              placeholder="Detalhamento do motivo, objetivos, requisitos específicos ou sub-etapas desta tarefa..."
            />
          </div>

          {/* Origem da Tarefa / Mensagem Original */}
          <div class="p-3.5 bg-cyan-950/20 border border-cyan-500/20 rounded-2xl flex items-center justify-between gap-3 text-xs">
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
                <HelpCircle class="w-4 h-4 text-cyan-400" />
              </div>
              <div class="min-w-0">
                <span class="block text-[11px] font-bold text-cyan-300">Origem e Contexto da Mensagem</span>
                <span class="block text-[10px] text-slate-400 truncate">
                  {task.source_message_id
                    ? `Extraído da mensagem: ID #${task.source_message_id}`
                    : `Extraído do histórico de conversa de ${task.stakeholder_name || task.contact_phone}`}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleGoToChat}
              class="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 text-[11px] font-bold rounded-xl transition shrink-0 flex items-center gap-1 cursor-pointer"
            >
              <MessageSquare class="w-3.5 h-3.5" />
              <span>Ver no Chat</span>
            </button>
          </div>

          {/* Quick Selectors Grid */}
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            
            {/* Status */}
            <div>
              <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus((e.target as HTMLSelectElement).value as any)}
                class="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500 cursor-pointer"
              >
                <option value="pending">Pendente</option>
                <option value="in_progress">Em Andamento</option>
                <option value="completed">Concluída</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>

            {/* Prioridade */}
            <div>
              <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Prioridade
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority((e.target as HTMLSelectElement).value as any)}
                class="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500 cursor-pointer"
              >
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
              </select>
            </div>

            {/* Responsável */}
            <div>
              <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1">
                <User class="w-3 h-3 text-slate-400" />
                <span>Responsável</span>
              </label>
              <input
                type="text"
                value={assignee}
                onInput={(e) => setAssignee((e.target as HTMLInputElement).value)}
                class="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500"
                placeholder="Você ou Nome"
              />
            </div>

            {/* Prazo */}
            <div>
              <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1">
                <Calendar class="w-3 h-3 text-slate-400" />
                <span>Data Limite</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onInput={(e) => setDueDate((e.target as HTMLInputElement).value)}
                class="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Markdown Rich Notes Area */}
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Informações & Notas Ricas (Suporta Markdown)
              </label>
              <div class="flex items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
                <button
                  type="button"
                  onClick={() => setActiveNotesTab('edit')}
                  class={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 cursor-pointer ${
                    activeNotesTab === 'edit' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Edit3 class="w-3 h-3" />
                  <span>Editar</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveNotesTab('preview')}
                  class={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 cursor-pointer ${
                    activeNotesTab === 'preview' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Eye class="w-3 h-3" />
                  <span>Visualizar</span>
                </button>
              </div>
            </div>

            {activeNotesTab === 'edit' ? (
              <textarea
                value={notes}
                onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
                rows={5}
                class="w-full p-4 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 font-mono focus:outline-none focus:border-cyan-500 transition leading-relaxed"
                placeholder="Escreva detalhes adicionais, observações ou cole trechos em Markdown..."
              />
            ) : (
              <div
                class="w-full min-h-[120px] p-4 bg-slate-950/80 border border-slate-800 rounded-2xl prose prose-invert max-w-none text-slate-200 leading-relaxed overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(notes || '_Nenhuma nota adicionada ainda._') }}
              />
            )}
          </div>

          {/* Links & Attachments Manager */}
          <div class="space-y-3">
            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <LinkIcon class="w-3.5 h-3.5 text-cyan-400" />
              <span>Links e Documentos Anexados</span>
            </label>

            {/* Link Input Row */}
            <div class="flex items-center gap-2">
              <input
                type="url"
                value={newLinkUrl}
                onInput={(e) => setNewLinkUrl((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLink())}
                placeholder="Cole um link externo (ex: https://docs.google.com/...)"
                class="flex-1 px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none focus:border-cyan-500"
              />
              <button
                type="button"
                onClick={handleAddLink}
                class="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-xl transition flex items-center gap-1 cursor-pointer shrink-0"
              >
                <Plus class="w-4 h-4" />
                <span>Adicionar</span>
              </button>
            </div>

            {/* Links List */}
            {linksList.length > 0 && (
              <div class="space-y-2 pt-1">
                {linksList.map((url, idx) => (
                  <div key={idx} class="flex items-center justify-between p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs gap-3">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-cyan-400 hover:underline truncate flex items-center gap-1.5"
                    >
                      <LinkIcon class="w-3.5 h-3.5 shrink-0" />
                      <span class="truncate">{url}</span>
                    </a>
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(idx)}
                      class="p-1 text-slate-500 hover:text-red-400 transition cursor-pointer shrink-0"
                      title="Remover link"
                    >
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Footer Actions */}
        <div class="p-4 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between gap-3 shrink-0">
          {showDeleteConfirm ? (
            <div class="flex items-center gap-2 animate-fade-in w-full justify-between">
              <span class="text-xs font-bold text-red-400 flex items-center gap-1.5">
                <AlertCircle class="w-4 h-4" />
                <span>Confirmar exclusão permanente?</span>
              </span>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  class="px-3.5 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded-xl transition shadow-lg flex items-center gap-1.5 cursor-pointer"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                  <span>Sim, Excluir</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                class="px-3.5 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 font-bold rounded-xl transition flex items-center gap-1.5 cursor-pointer"
                title="Excluir tarefa permanentemente do sistema"
              >
                <Trash2 class="w-4 h-4" />
                <span class="hidden xs:inline">Excluir Tarefa</span>
              </button>

              <div class="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleClose}
                  class="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl transition cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  class="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-xl shadow-lg transition flex items-center gap-2 cursor-pointer"
                >
                  <Save class="w-4 h-4" />
                  <span>Salvar Alterações</span>
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
